'use client';

import type { StaticImageData } from 'next/image';
import Image from 'next/image';
import type { ComponentProps, ReactNode } from 'react';

import type { ModalProps } from '../components';
import { CircleCloseButton, ModalBase } from '../components';
import { VisualModalBody, VisualModalBodyHead } from './components';
import styles from './visual-modal.component.module.scss';

// memo:
// モーダルは実装がバラツキやすいので、型を厳しめにしています
// マーケに重点が置かれているような、デザインシステム外のモーダルを作る必要がある場合は、ModalBase をベースに別途モーダルコンポーネントを作成してください

type Props = Omit<ModalProps, 'children' | 'isOpen'> & {
  /**
   * 画像の alt 属性を指定します
   */
  alt: string;
  /**
   * 画像の src を指定します
   */
  src: string | StaticImageData;
  /**
   * aria-label と タイトル を指定します。 aria-label は `ReactModal.Props.contentLabel` により上書きできます。
   */
  title: string;
  /**
   * タイトルをJSXで描画したいときに使用します。renderTitle が指定されている場合、title は無視されます (aria-label としての title は有効なままです)
   */
  renderTitle?: (() => ReactNode) | ReactNode;
  /**
   * モーダルのボディコンテンツです
   */
  body: (() => ReactNode) | ReactNode;
  /**
   * ボディ のレイアウトを指定します
   */
  bodyLayout?: ComponentProps<typeof VisualModalBody>['layout'];
  /**
   * モーダルの閉じるボタンを自動フォーカスするかどうかを指定します
   */
  autoFocusCloseButton?: boolean;
  /**
   * VisualModalBodyHead（タイトル部分）を非表示にするかどうかを指定します
   * true の場合、title と renderTitle は無視され、body 内で見出しを自由に実装できます
   * @remarks
   * デザイン要件を満たすために他の手段がない場合のエスケープハッチとしてのみ利用してください。
   * 基本的にはデフォルトのタイトル表示を活用し、どうしても要件を満たせない場合のみ使用することを推奨します。
   */
  hideHeadTitle?: boolean;
  isOpen?: boolean; // 既存の実装を踏襲
};

export const VisualModal = ({
  alt,
  src,
  title,
  renderTitle,
  body,
  bodyLayout,
  className,
  autoFocusCloseButton = true,
  hideHeadTitle = false,
  isOpen = true,
  ...rest
}: Props) => {
  return (
    <ModalBase
      className={[className, styles.visualModal].filter(Boolean).join(' ')}
      contentLabel={title}
      isOpen={isOpen}
      {...rest}
    >
      <CircleCloseButton
        aria-label="閉じる"
        autoFocus={autoFocusCloseButton}
        type="button"
        onClick={rest.onRequestClose}
      />
      <div className={styles.imageWrapper}>
        {alt ? (
          <h2 style={{ borderRadius: 'inherit' }}>
            {/* memo: width={1200} height={628} は、コムデザが作成する元の画像サイズであり、アスペクト比 1.91:1 に準拠しています */}
            <Image alt={alt} height={628} src={src} width={1200} />
          </h2>
        ) : (
          <Image alt={alt} height={628} src={src} width={1200} />
        )}
      </div>
      <VisualModalBody layout={bodyLayout}>
        {!hideHeadTitle && (
          <VisualModalBodyHead>
            {renderTitle
              ? typeof renderTitle === 'function'
                ? renderTitle()
                : renderTitle
              : title}
          </VisualModalBodyHead>
        )}
        {typeof body === 'function' ? body() : body}
      </VisualModalBody>
    </ModalBase>
  );
};
